import { openDB, DBSchema, IDBPDatabase } from "idb";
import moment from "moment";
import {
  OfflineRechargeStatus,
  Order,
  OrderLog,
  PedidoPosOrderLog,
  PrintStatus,
  ProdutoPedidoPosOrderLog,
  RePrintOrderSyncObject,
} from "../../../domains/order/agreggates/order/Order";
import { IOrderRepositoryDB } from "../../../domains/order/interfaces/repositories/IOrderRepositoryDB";

interface MeepOrdersSchema extends DBSchema {
  orders: {
    key: string;
    value: OrderLog;
    indexes: {
      "by-sessionId": string;
      "by-offlineRechargeStatus": number;
      "by-createdAt": string;
    };
  };
}

const DB_NAME = "meep-orders";
const DB_VERSION = 1;
const STORE = "orders" as const;

let dbPromise: Promise<IDBPDatabase<MeepOrdersSchema>> | null = null;

const getDb = (): Promise<IDBPDatabase<MeepOrdersSchema>> => {
  if (!dbPromise) {
    dbPromise = openDB<MeepOrdersSchema>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        const store = db.createObjectStore(STORE, { keyPath: "id" });
        store.createIndex("by-sessionId", "sessionId");
        store.createIndex("by-offlineRechargeStatus", "offlineRechargeStatus");
        store.createIndex("by-createdAt", "createdAt");
      },
    });
  }
  return dbPromise;
};

const add = async (
  order: Order,
  log: string,
  pix?: string,
  printStatus?: PrintStatus
): Promise<void> => {
  try {
    const db = await getDb();
    const existing = await db.get(STORE, order.id.toLowerCase());

    if (existing) {
      const pixLogs = [...(existing.pixLog ?? [])];
      const logs = [...(existing.log ?? [])];

      if (pix) {
        pixLogs.push({ time: moment().utc().format(), message: log, pix });
      } else {
        logs.push({ time: moment().utc().format(), message: log });
      }

      const orderLog = order as OrderLog;
      let orderItems = orderLog.orderItems ?? existing.orderItems;

      if (orderLog.pedidoPOS) {
        orderItems.forEach((it) => {
          orderLog.pedidoPOS!.produtosObject.forEach((pos) => {
            if (pos.produtoId === it.productId) {
              it.ticketIds = it.ticketIds || [];
              if (!it.ticketIds.includes(pos.ticketId)) {
                const existsInOther = orderItems.some((o) =>
                  o.ticketIds?.includes(pos.ticketId)
                );
                if (!existsInOther) {
                  it.ticketIds.push(pos.ticketId);
                }
              }
            }
          });
        });
      }

      const updated: OrderLog = {
        ...existing,
        isDataRecorded:
          "isDataRecorded" in order
            ? (order as OrderLog).isDataRecorded
            : existing.isDataRecorded,
        isOfflineRecharge: orderLog.isOfflineRecharge ?? existing.isOfflineRecharge,
        offlineRechargeStatus:
          orderLog.offlineRechargeStatus ?? existing.offlineRechargeStatus,
        isSynchronized: orderLog.isSynchronized ?? existing.isSynchronized,
        payment: orderLog.payment ?? existing.payment,
        paymentPix: orderLog.paymentPix ?? existing.paymentPix,
        pedidoPOS: orderLog.pedidoPOS ?? existing.pedidoPOS,
        paymentApproved: orderLog.paymentApproved ?? existing.paymentApproved,
        nfce: orderLog.nfce ?? existing.nfce,
        orderItems,
        totalValue: orderLog.totalValue ?? existing.totalValue,
        log: logs,
        pixLog: pixLogs,
        printStatus: printStatus ?? existing.printStatus,
        printType: orderLog.printType ?? existing.printType,
        rechargeType: orderLog.rechargeType ?? existing.rechargeType,
      };

      await db.put(STORE, updated);
    } else {
      const newOrder: OrderLog = {
        ...(order as OrderLog),
        id: order.id.toLowerCase(),
        log: pix ? [] : [{ time: moment().utc().format(), message: log }],
        pixLog: pix
          ? [{ time: moment().utc().format(), message: log, pix }]
          : [],
        printStatus: printStatus,
      };
      await db.put(STORE, newOrder);
    }
  } catch (error) {
    console.error("[OrderRepositoryV2] add failed", error);
  }
};

const findById = async (id: string): Promise<OrderLog | null> => {
  try {
    const db = await getDb();
    return (await db.get(STORE, id.toLowerCase())) ?? null;
  } catch (error) {
    console.error("[OrderRepositoryV2] findById failed", error);
    return null;
  }
};

const all = async (): Promise<OrderLog[]> => {
  try {
    const db = await getDb();
    const orders = await db.getAllFromIndex(STORE, "by-createdAt");
    return orders.reverse();
  } catch (error) {
    console.error("[OrderRepositoryV2] all failed", error);
    return [];
  }
};

const findPendingRecharge = async (
  associationId: string
): Promise<OrderLog | null> => {
  try {
    const db = await getDb();
    const orders = await db.getAllFromIndex(
      STORE,
      "by-offlineRechargeStatus",
      OfflineRechargeStatus.RECHARGE
    );
    return (
      orders.find((o) => o.customer?.associationId === associationId) ?? null
    );
  } catch (error) {
    console.error("[OrderRepositoryV2] findPendingRecharge failed", error);
    return null;
  }
};

const getAllPendingRecharge = async (): Promise<OrderLog[]> => {
  try {
    const db = await getDb();
    return await db.getAllFromIndex(
      STORE,
      "by-offlineRechargeStatus",
      OfflineRechargeStatus.RECHARGE
    );
  } catch (error) {
    console.error("[OrderRepositoryV2] getAllPendingRecharge failed", error);
    return [];
  }
};

const getOrdersByIds = async (ids: string[]): Promise<OrderLog[]> => {
  try {
    const db = await getDb();
    const orders = await Promise.all(
      ids.map((id) => db.get(STORE, id.toLowerCase()))
    );
    return orders.filter(Boolean) as OrderLog[];
  } catch (error) {
    console.error("[OrderRepositoryV2] getOrdersByIds failed", error);
    return [];
  }
};

const TWO_DAYS_MS = 2 * 24 * 60 * 60 * 1000;

const clearOldSessionOrders = async (sessionId: string): Promise<void> => {
  try {
    const db = await getDb();
    const tx = db.transaction(STORE, "readwrite");
    const cutoff = Date.now() - TWO_DAYS_MS;
    let cursor = await tx.store.openCursor();

    while (cursor) {
      const order = cursor.value;
      const isOldSession = order.sessionId !== sessionId;
      const isOlderThanTwoDays =
        !order.createdAt || new Date(order.createdAt).getTime() < cutoff;

      if (isOldSession || isOlderThanTwoDays) {
        await cursor.delete();
      }
      cursor = await cursor.continue();
    }

    await tx.done;
  } catch (error) {
    console.error("[OrderRepositoryV2] clearOldSessionOrders failed", error);
  }
};

const updateRePrintTicket = async (
  ticketId: string,
  rePrintRequest: RePrintOrderSyncObject
): Promise<void> => {
  try {
    const db = await getDb();
    const tx = db.transaction(STORE, "readwrite");
    let cursor = await tx.store.openCursor();

    while (cursor) {
      const orderLog = cursor.value;
      const produtosObject = orderLog.pedidoPOS?.produtosObject;
      if (produtosObject?.some((p) => p?.ticketId === ticketId)) {
        const updated: OrderLog = {
          ...orderLog,
          pedidoPOS: {
            ...orderLog.pedidoPOS!,
            produtosObject: produtosObject.map((p) => {
              if (p?.ticketId !== ticketId) return p;
              return {
                ...p,
                reprinted: p.reprinted
                  ? [...p.reprinted, rePrintRequest]
                  : [rePrintRequest],
              } as ProdutoPedidoPosOrderLog;
            }),
          } as PedidoPosOrderLog,
        };
        await cursor.update(updated);
        break;
      }
      cursor = await cursor.continue();
    }

    await tx.done;
  } catch (error) {
    console.error("[OrderRepositoryV2] updateRePrintTicket failed", error);
  }
};

const updateRePrintTicketList = async (
  ticketIds: string[],
  rePrintRequest: RePrintOrderSyncObject
): Promise<void> => {
  try {
    const db = await getDb();
    const ids = new Set(ticketIds);
    const tx = db.transaction(STORE, "readwrite");
    let cursor = await tx.store.openCursor();

    while (cursor) {
      const orderLog = cursor.value;
      const produtosObject = orderLog.pedidoPOS?.produtosObject;
      if (produtosObject?.some((p) => p && ids.has(p.ticketId))) {
        const updated: OrderLog = {
          ...orderLog,
          pedidoPOS: {
            ...orderLog.pedidoPOS!,
            produtosObject: produtosObject.map((p) => {
              if (!p || !ids.has(p.ticketId)) return p;
              return {
                ...p,
                reprinted: p.reprinted
                  ? [...p.reprinted, rePrintRequest]
                  : [rePrintRequest],
              } as ProdutoPedidoPosOrderLog;
            }),
          } as PedidoPosOrderLog,
        };
        await cursor.update(updated);
      }
      cursor = await cursor.continue();
    }

    await tx.done;
  } catch (error) {
    console.error("[OrderRepositoryV2] updateRePrintTicketList failed", error);
  }
};

const putRaw = async (order: OrderLog): Promise<void> => {
  try {
    const db = await getDb();
    await db.put(STORE, { ...order, id: order.id.toLowerCase() });
  } catch (error) {
    console.error("[OrderRepositoryV2] putRaw failed", error);
    throw error;
  }
};

export const OrderRepositoryLocalStorageV2 = () => ({
  add,
  findById,
  all,
  updateRePrintTicket,
  updateRePrintTicketList,
  findPendingRecharge,
  getAllPendingRecharge,
  clearOldSessionOrders,
  getOrdersByIds,
  putRaw,
});
